import React, { useState } from "react";
import {
  AccountCircle,
  Description,
  Help,
  Close,
  ExpandMore,
} from "@mui/icons-material";
import { useUserSettings } from "../../context/UserSettingsContext";
import TemplateSettings from "./SettingsSections/TemplateSettings";
import { useUser } from "../../routing/UserContext";

function UserSettings() {
  const { isOpen, closeSettings } = useUserSettings();
  const [activeSection, setActiveSection] = useState("profile");
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { userData, companyData } = useUser();

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking directly on the backdrop (not the content)
    if (e.target === e.currentTarget) {
      closeSettings();
    }
  };

  const menuItems = [
    { id: "profile", label: "Perfil", icon: AccountCircle },
    { id: "templates", label: "Plantillas", icon: Description },
    { id: "help", label: "Ayuda", icon: Help },
  ];

  const faqItems = [
    {
      question: "¿Cómo publico una vacante?",
      answer:
        "Inicia sesión, haz clic en 'Nueva vacante', completa la información requerida, valida y publica. ¡Así de fácil!",
    },
    {
      question:
        "¿Cómo puedo compartir mi vacante en redes sociales o bolsas de empleo?",
      answer:
        "En tu panel de vacantes, usa el botón 'Compartir' para copiar el enlace o compartir directamente en redes sociales, grupos o bolsas de empleo.",
    },
    {
      question: "¿Qué hago si no he recibido postulantes en mi vacante?",
      answer:
        "Puedes aumentar la visibilidad usando Laburo Boost (contacta a tu ejecutivo/a de cuenta) o compartiendo el enlace de la vacante en redes y grupos para redirigir postulantes.",
    },
  ];

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl w-[800px] h-[600px] flex overflow-hidden relative">
        <button
          onClick={closeSettings}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-[101]"
        >
          <Close />
        </button>
        {/* Sidebar */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 p-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              Configuración
            </h2>
          </div>
          <nav className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === item.id
                      ? "bg-blue-50 text-blue-600"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Icon style={{ fontSize: "20px" }} />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-2xl">
            {activeSection === "profile" && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Perfil
                </h3>

                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xl font-medium">
                    {userData?.firstName?.charAt(0)}
                    {userData?.lastName?.charAt(0)}
                  </div>

                  <div className="space-y-1">
                    <h4 className="text-lg font-medium text-gray-900">
                      {userData?.firstName} {userData?.lastName}
                    </h4>
                    <p className="text-sm text-gray-500">{userData?.user}</p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Empresa
                  </h4>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-400 to-cyan-400 flex items-center justify-center text-white text-sm font-medium">
                      {companyData?.name?.charAt(0)}
                    </div>
                    <p className="text-gray-900">{companyData?.name}</p>
                  </div>
                </div>
              </div>
            )}
            {activeSection === "templates" && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Plantillas
                </h3>
                <div className="space-y-4">
                  <TemplateSettings />
                </div>
              </div>
            )}
            {activeSection === "help" && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Ayuda y Soporte
                </h3>

                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">
                      ¿Necesitas ayuda?
                    </h4>
                    <p className="text-sm text-blue-700">
                      Estamos aquí para ayudarte. Nuestro equipo de soporte está
                      disponible para responder tus preguntas 24/7.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-700">
                      Preguntas frecuentes
                    </h4>
                    <div className="space-y-2">
                      {faqItems.map((item, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg overflow-hidden"
                        >
                          <button
                            onClick={() => toggleFaq(index)}
                            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                          >
                            <span className="text-sm font-medium text-gray-800 text-left">
                              {item.question}
                            </span>
                            <ExpandMore
                              className={`transform transition-transform duration-200 ${
                                openFaq === index ? "rotate-180" : ""
                              }`}
                              style={{ fontSize: "20px" }}
                            />
                          </button>
                          {openFaq === index && (
                            <div className="px-4 pb-4">
                              <p className="text-sm text-gray-600">
                                {item.answer}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Contacto
                    </h4>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <svg
                            className="w-5 h-5 text-green-600"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Tu ejecutivo/a de cuenta
                          </p>
                          <p className="text-sm text-gray-500">
                            +593 98 351 9572
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-blue-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Soporte
                          </p>
                          <p className="text-sm text-gray-500">
                            support@quierolaburo.com
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserSettings;
